import React, { useState, useEffect, useCallback } from "react";
import {
  Autocomplete,
  TextField,
  Box,
  Alert,
  Button,
  CircularProgress,
  debounce,
  Typography,
  FormHelperText,
  Chip,
} from "@mui/material";
import { MedicationRounded, SpellcheckRounded, VerifiedRounded } from "@mui/icons-material";
import { useDebounce } from "use-debounce";
import { Trans, useTranslation } from "react-i18next";
import { rxTermsService } from "../../../services/rxTermsService";
import { drugReferenceService } from "services/drugReferenceService";

const MedicationSearch = ({
  value,
  onChange,
  onSelect,
  disabled = false,
  error = null,
  helperText = "",
  // Rendered as the TextField's own label so it notches the outlined border like
  // every other field in the dialog. It used to be a separate <InputLabel> above
  // the box, which is the shape an underlined input wants, not a bordered one.
  label = "",
  placeholder,
  fullWidth = true,
  // The dialog that opens for one purpose should open with the cursor in the
  // field that purpose lives in. Off by default so nothing else changes.
  autoFocus = false,
}) => {
  const { t } = useTranslation("medications");
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Debounce inputValue for showing manual entry alert (reduces visual noise)
  const [debouncedInputValue] = useDebounce(inputValue, 500);

  // Debounced search function using verified medication database
  const debouncedSearch = useCallback(
    debounce(async (searchTerm) => {
      if (!searchTerm || searchTerm.length < 2) {
        setOptions([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setSearchError(null);

      try {
        const medications = await rxTermsService.searchMedications(
          searchTerm,
          10
        );
        setOptions(medications);
        setHasSearched(true);
      } catch (error) {
        console.error("Medication search error:", error);
        setSearchError(t("search.failed"));
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300),
    []
  );

  // Effect to sync inputValue with value prop changes (e.g., when parent clears or sets medication)
  useEffect(() => {
    if (value) {
      if (typeof value === "string") {
        setInputValue(value);
      } else if (typeof value === "object" && value?.name) {
        const { cleanName } = parseMedicationName(
          value.displayName || value.name
        );
        setInputValue(cleanName);
      }
    } else {
      setInputValue("");
    }
  }, [value]);

  // Effect to trigger search when input changes
  useEffect(() => {
    if (inputValue) {
      debouncedSearch(inputValue);
    } else {
      setOptions([]);
      setLoading(false);
    }
  }, [inputValue, debouncedSearch]);

  // Handle input change
  const handleInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);

    if (onChange) {
      // If user is manually typing and we currently have a verified medication selected,
      // we need to check if the typed text still matches the verified medication
      if (typeof value === "object" && value?.rxcui) {
        const { cleanName } = parseMedicationName(
          value.displayName || value.name
        );

        // If the typed text doesn't match the selected verified medication name,
        // clear the verified data and just pass the string
        if (newInputValue !== cleanName) {
          onChange(newInputValue); // Pass just the string, clearing verified metadata
          // CRITICAL: Also call onSelect to clear the parent's selectedMedication state
          if (onSelect) {
            onSelect(newInputValue);
          }
        } else {
          // Text still matches: keep the verified pick (the parent holds it via
          // onSelect) and hand the form field the name, never the object, which
          // broke `name.trim()` on save.
          onChange(cleanName);
        }
      } else {
        // No verified object selected, just pass the string
        onChange(newInputValue);
      }
    }
  };

  // Taking a spelling only ever rewrites the field. The search that follows is the one
  // thing that can turn a name into a medication on the record, and a typo corrected for
  // the patient is still not a drug they confirmed.
  const applySuggestedSpelling = (event, name) => {
    handleInputChange(event, name);
  };

  // Below the message rather than in the Alert's action slot, which is sized by whatever
  // the text leaves over and squeezes the label into a column. Nothing pulls the button
  // left to align with the sentence: MuiAlert-message overflows auto, so a negative margin
  // wider than the button's own padding clips the first letter off the label.
  const suggestionButton = (name) => (
    <Box sx={{ mt: 1 }}>
      {/* The theme defaults buttons to text, which reads as part of the alert's own pale
          blue. Contained on purpose here: primary.main is dark enough to separate from that
          tint on its own, and its hover and focus states come with it. */}
      <Button
        size="small"
        variant="contained"
        onClick={(event) => applySuggestedSpelling(event, name)}
      >
        {t("search.useSuggested")}
      </Button>
    </Box>
  );

  // Handle option selection
  const handleChange = (event, newValue) => {
    // A "Did you mean" option is not a medication: picking it retypes the field with the
    // suggested spelling, which runs the search again.
    if (newValue?.kind === "suggestion") {
      applySuggestedSpelling(event, newValue.name);
      return;
    }
    if (onSelect) {
      onSelect(newValue);
    }
  };

  // Parse brand name from medication name (removes brackets)
  const parseMedicationName = (name) => {
    if (!name) return { cleanName: "", brandName: "" };

    const brandMatch = name.match(/^(.+?)\s*\[([^\]]+)\]$/);
    if (brandMatch) {
      return {
        cleanName: brandMatch[1].trim(),
        brandName: brandMatch[2].trim(),
      };
    }
    return { cleanName: name, brandName: "" };
  };

  // Render medication option with verified data
  const renderOption = (props, option, { index }) => {
    if (option.kind === "suggestion") {
      const { key, ...rest } = props;
      return (
        <Box component="li" key={`${option.rxcui}-${index}`} {...rest} sx={{ p: 2 }}>
          <SpellcheckRounded sx={{ mr: 2, color: "text.secondary" }} />
          <Typography variant="body1">
            <Trans
              ns="medications"
              i18nKey="search.didYouMean"
              values={{ name: option.name }}
              components={{ 1: <strong /> }}
            />
          </Typography>
        </Box>
      );
    }

    // Use index + rxcui for truly unique keys
    const uniqueKey = `${option.rxcui}-${index}`;
    const { key, ...otherProps } = props;
    const { cleanName, brandName } = parseMedicationName(
      option.displayName || option.name
    );

    return (
      <Box component="li" key={uniqueKey} {...otherProps} sx={{ p: 2 }}>
        <MedicationRounded sx={{ mr: 2, color: "primary.main" }} />
        <Box sx={{ flexGrow: 1 }}>
          <Typography
            variant="body1"
            sx={{
              fontWeight: "medium",
              lineHeight: 1.25,
              mb: 0.4,
            }}
          >
            {cleanName}
          </Typography>
          {brandName && (
            <Typography
              variant="body2"
              color="primary.main"
              sx={{
                fontWeight: 500,
                lineHeight: 1.2,
                mb: 0.5,
              }}
            >
              {t("search.brand", { brand: brandName })}
            </Typography>
          )}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              lineHeight: 1.1,
              display: "block",
            }}
          >
            {option.type}
          </Typography>
        </Box>
      </Box>
    );
  };

  // Get display value for selected option (clean name without brackets)
  const getOptionLabel = (option) => {
    if (typeof option === "string") {
      return option;
    }
    const name = option.displayName || option.name || "";
    const { cleanName } = parseMedicationName(name);
    return cleanName;
  };

  // Determine if options are equal
  const isOptionEqualToValue = (option, value) => {
    if (typeof option === "string" && typeof value === "string") {
      return option === value;
    }
    if (typeof option === "object" && typeof value === "object") {
      return option.rxcui === value.rxcui;
    }
    return false;
  };

  // For a typed name the search list doesn't have: do we still recognize it (a brand RxNorm
  // knows, an international name, an abbreviation — the drugAliases collection)? If not, what
  // does RxNorm think was meant? A typo is better fixed by the patient than saved, and better
  // than us guessing: a wrong guess would put another drug's warnings on their record.
  const [recognized, setRecognized] = useState(null); // { input, alias }
  const [suggestions, setSuggestions] = useState({ input: null, list: [] });
  const typed = (debouncedInputValue || "").trim();
  const hasVerifiedSelection = !!(value && typeof value === "object" && value?.rxcui);
  const optionCount = (options ?? []).length;

  useEffect(() => {
    if (hasVerifiedSelection || typed.length < 3) {
      setRecognized(null);
      setSuggestions({ input: null, list: [] });
      return undefined;
    }
    let live = true;
    drugReferenceService.getAlias(typed).then((alias) => {
      if (!live) return;
      setRecognized(alias?.referenceKey ? { input: typed, alias } : null);
      // Suggestions only when nothing else answered the name.
      if (alias?.referenceKey || optionCount) {
        setSuggestions({ input: null, list: [] });
        return;
      }
      // A nicety: whatever comes back (or doesn't), the field keeps working.
      Promise.resolve(rxTermsService.getSpellingSuggestions?.(typed))
        .then((list) => {
          if (!live) return;
          const lower = typed.toLowerCase();
          setSuggestions({
            input: typed,
            list: (Array.isArray(list) ? list : [])
              .filter((s) => s.toLowerCase() !== lower)
              .slice(0, 3),
          });
        })
        .catch(() => {});
    });
    return () => {
      live = false;
    };
  }, [typed, hasVerifiedSelection, optionCount]);

  // "Sulfamethoxazole/Trimethoprim (Oral Pill)" → "Sulfamethoxazole/Trimethoprim"
  const recognizedAs = recognized?.alias?.meaning?.replace(/\s*\([^)]*\)\s*$/, "");
  const showRecognized = recognized && recognized.input === typed;
  const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

  // When the search finds nothing, RxNorm's spellings become the dropdown's options. They have
  // to be options: in freeSolo mode MUI never shows the empty-state text (noOptionsText), so a
  // patient looking at the dropdown would otherwise see nothing at all. Only for what is in the
  // field right now — a suggestion for an earlier spelling is noise.
  const suggestionOptions =
    !(options ?? []).length && !showRecognized && suggestions.input === inputValue.trim()
      ? suggestions.list.map((s) => ({
          kind: "suggestion",
          name: capitalize(s),
          rxcui: `suggestion:${s}`,
        }))
      : [];
  const shownOptions = (options ?? []).length ? options : suggestionOptions;

  // The same first suggestion, offered under the field as well: the dropdown's "Did you
  // mean" options are only ever seen by a patient who thinks to open the dropdown, and a
  // misspelled name is exactly the case where they have stopped looking at it.
  const suggestedSpelling =
    !optionCount && !showRecognized && suggestions.input === typed && suggestions.list.length
      ? capitalize(suggestions.list[0])
      : null;

  // The generic behind a recognized brand, offered on that alert as the same button. Keeping
  // their own name costs them nothing — getReferenceFor resolves it through the alias, so the
  // label, the dose ceiling and the allergy checks are the generic's either way — so this is
  // only here for a patient who would rather see the generic on their record.
  const recognizedSwap =
    showRecognized && recognizedAs && recognizedAs.toLowerCase() !== typed.toLowerCase()
      ? capitalize(recognizedAs)
      : null;

  return (
    <Box>
      <Autocomplete
        value={value}
        onChange={handleChange}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        options={shownOptions}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={isOptionEqualToValue}
        renderOption={renderOption}
        loading={loading}
        disabled={disabled}
        fullWidth={fullWidth}
        freeSolo
        selectOnFocus
        handleHomeEndKeys
        filterOptions={(x) => x} // Disable built-in filtering since we handle it via API
        renderInput={(params) => (
          <TextField
            {...params}
            autoFocus={autoFocus}
            label={label || undefined}
            placeholder={placeholder ?? t("search.placeholder")}
            error={!!error}
            helperText={helperText}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress color="inherit" size={20} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        noOptionsText={
          loading ? (
            <Box sx={{ display: "flex", alignItems: "center", p: 2 }}>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              {t("search.searching")}
            </Box>
          ) : hasSearched && inputValue && inputValue.length >= 2 ? (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {t("search.noResults", { input: inputValue })}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {t("search.startTyping")}
              </Typography>
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ mt: 1, display: "block" }}
              >
                {t("search.poweredBy")}
              </Typography>
            </Box>
          )
        }
        sx={{
          "& .MuiAutocomplete-option": {
            alignItems: "flex-start",
          },
        }}
      />

      {searchError && (
        <FormHelperText error sx={{ mt: 1 }}>
          {searchError}
        </FormHelperText>
      )}

      {/* Show verification OR manual entry alert - never both */}
      {value &&
      typeof value === "object" &&
      value?.rxcui &&
      inputValue &&
      inputValue ===
        parseMedicationName(value.displayName || value.name).cleanName ? (
        <Alert severity="success" sx={{ mt: 1 }}>
          {t("search.verified", {
            name: parseMedicationName(value.displayName || value.name).cleanName,
          })}
        </Alert>
      ) : (
        debouncedInputValue &&
        debouncedInputValue.length >= 2 &&
        !loading &&
        (showRecognized ? (
          <Alert severity="info" sx={{ mt: 1 }}>
            <Trans
              ns="medications"
              i18nKey="search.recognized"
              values={{
                typed,
                meaning: recognizedAs,
                brandNote: recognized.alias.country
                  ? t("info.brandSoldIn", { country: recognized.alias.country })
                  : "",
              }}
              components={{ 1: <strong /> }}
            />
            {/* A registry's licence requires citing it, with its update date. */}
            {recognized.alias.attribution && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                {t("info.source", { source: recognized.alias.attribution })}
              </Typography>
            )}
            {recognizedSwap && suggestionButton(recognizedSwap)}
          </Alert>
        ) : suggestedSpelling ? (
          <Alert severity="info" sx={{ mt: 1 }}>
            <Trans
              ns="medications"
              i18nKey="search.notVerifiedDidYouMean"
              values={{ typed, name: suggestedSpelling }}
              components={{ 1: <strong /> }}
            />
            {suggestionButton(suggestedSpelling)}
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mt: 1 }}>
            {t("search.manual", { input: debouncedInputValue })}
          </Alert>
        ))
      )}
    </Box>
  );
};

export default MedicationSearch;
